import OtherPageHeader from "../../components/OtherHeader";
import { Button, Form, message, Modal, Spin, Progress, Checkbox } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import React, { useEffect, useState } from "react";
import AxiosInstance from "../../AxiosInstance";
import "./LoanTrack.css";
import { getIpAddress } from "../../utils/getIpAddress";

import EmailValidation from "./EmailValidation";
import PanValidation from "./PanValidation";
import LoanForm from "./LoanForm";

/* ------------------ NO UI STEPS ------------------- */
const NO_UI_STEPS = [];

/* ------------------ KYC STATIC CONSENTS ------------------- */
const KYC_STATIC_CONSENTS = [
  {
    key: "kyc_verification",
    isMandatory: true,
    matchText: "consent for KYC verification",
    message: "I hereby provide my explicit consent for KYC verification and authorize the use of my details/documents for the purpose of identity verification and related processing.",
  },
];

/* ------------------ KYC CONSENT API FUNCTIONS ------------------- */
const getKycConsentsFromApi = async () => {
  try {
    const response = await AxiosInstance().get("/loan-consent/filter", {
      params: { screenName: "KYC", isActive: true },
    });
    if (response.data?.success) return response.data.data;
    return [];
  } catch (error) {
    return [];
  }
};

const getUserConsentsByMobile = async (mobileNumber) => {
  try {
    const response = await AxiosInstance().get("/loan-consent/consents-by-mobile", {
      params: { mobileNumber },
    });
    if (response.data?.success) return response.data.data.consents;
    return [];
  } catch (error) {
    return [];
  }
};

/* ------------------ MERGED STEPS CONFIG ------------------ */
const MERGED_STEPS_CONFIG = {
  "LOAN APPLICATION": { group: "validation", order: 0 },
  "PAN VALIDATION": { group: "validation", order: 1 },
  "CKYC": { group: "kyc", order: 0 },
  "KYC": { group: "kyc", order: 1 },
  "APPLICATION SUBMISSION": { group: "kyc", order: 2 },
  "E AGREEMENT": { group: "edata", order: 0 },
  "E MANDATE": { group: "edata", order: 1 },
  "VKYC": { group: "edata", order: 2 },
  "V KYC": { group: "edata", order: 2 } // Handle space variation
};

export default function LoanTrack() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loanData, setLoanData] = useState(null);
  const [loader, setLoader] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isReturning, setIsReturning] = useState(false);
  const [kycConsentStates, setKycConsentStates] = useState({});
  const [showKycConsentMsg, setShowKycConsentMsg] = useState(false);
  const [userData, setUserData] = useState(null);


  /* ------------------ FETCH LOAN ------------------ */
  useEffect(() => {
    if (!id) return;
    fetchLoan();
  }, [id]);

  /* ------------------ LOAD USER DATA FOR KYC CONSENTS ------------------ */
  useEffect(() => {
    if (!id) return;
    fetchUserDataForKycConsents();
  }, [id]);

  const fetchUserDataForKycConsents = async () => {
    try {
      const resp = await AxiosInstance().get("/user/me");
      const data = resp.data;
      setUserData(data);

      // Prefill consent states from user's existing consents if mobile is available
      if (data.phoneNumber && data.phoneNumber.length === 10) {
        const userConsents = await getUserConsentsByMobile(data.phoneNumber);
        const apiConsents = await getKycConsentsFromApi();
        const newStates = {};
        KYC_STATIC_CONSENTS.forEach((staticConsent) => {
          const apiMatch = apiConsents.find((c) =>
            c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "KYC"
          );
          if (apiMatch) {
            const userMatch = userConsents.find((uc) =>
              uc.consentId === apiMatch.consentId &&
              (uc.loanId === parseInt(id) || uc.loanId === null)
            );
            if (userMatch) newStates[staticConsent.key] = userMatch.consentStatus;
          }
        });
        setKycConsentStates(newStates);
      }
    } catch {
      // Error handling
    }
  };

  // Clear email check session when leaving loan track page
  useEffect(() => {
    return () => {
      // This runs when component unmounts (user navigates away)
      sessionStorage.removeItem('emailChecked');
    };
  }, []);

  const fetchLoan = async () => {
    try {
      setLoader(true);
      const res = await AxiosInstance().get(`/policy/loan/${id}`);

      if (!res?.data) {
        throw new Error("Invalid loan response");
      }

      setLoanData(res.data);
      
      // Only check email verification on initial load
      if (initialLoad) {
        const emailCheckedSession = sessionStorage.getItem('emailChecked');
        if (!emailCheckedSession) {
          const user = await AxiosInstance().get("/user/me");
          if (!user.data.isEmailVerified) {
            setShowEmailModal(true);
          } else {
            setEmailVerified(true);
          }
          sessionStorage.setItem('emailChecked', 'true');
        } else {
          setEmailVerified(true);
        }
        setInitialLoad(false);
      }
    } catch (error) {
      console.error("Loan fetch error:", error);
      message.error("Unable to load loan details");
    } finally {
      setLoader(false);
    }
  };

  const handleEmailVerificationSuccess = () => {
    setShowEmailModal(false);
    setEmailVerified(true);
    message.success("Email verified successfully!");
  };

  /* ------------------ KYC CONSENT HANDLING ------------------ */
  const handleKycConsentChange = (key, checked) => {
    setKycConsentStates((prev) => ({ ...prev, [key]: checked }));
  };

  const areKycMandatoryConsentsAccepted = () =>
    KYC_STATIC_CONSENTS.filter((c) => c.isMandatory).every((c) => kycConsentStates[c.key] === true);

  const submitKycConsentData = async () => {
    try {
      if (!userData || !userData.userId) {
        message.error("User data is incomplete. Please try again.");
        return false;
      }
      const ipAddress = await getIpAddress();
      const apiConsents = await getKycConsentsFromApi();
      const existingUserConsents = await getUserConsentsByMobile(userData.phoneNumber);

      const consentTransactions = KYC_STATIC_CONSENTS.map((staticConsent) => {
        const apiMatch = apiConsents.find((c) =>
          c.consentMessage?.includes(staticConsent.matchText) && c.screenName === "KYC"
        );

        if (!apiMatch) return null;

        const existingConsent = existingUserConsents.find(uc =>
          uc.consentId === apiMatch.consentId &&
          (uc.loanId === parseInt(id) || uc.loanId === null)
        );

        const consentData = {
          consentId: apiMatch.consentId,
          consentStatus: kycConsentStates[staticConsent.key] || false,
          isActive: true,
          userId: userData.userId,
          loanId: parseInt(id),
          mobileNumber: userData.phoneNumber,
          ipAddress: ipAddress
        };

        return {
          ...consentData,
          existingTransaction: existingConsent,
        };
      }).filter((t) => t && t.consentId !== null);

      for (const consentTx of consentTransactions) {
        try {
          if (consentTx.existingTransaction) {
            const updateData = {
              consentStatus: consentTx.consentStatus,
              ipAddress: ipAddress,
            };
            await AxiosInstance().put(`/loan-consent/transaction/${consentTx.existingTransaction.loanConsentId}`, updateData);
          } else {
            const createData = {
              ...consentTx,
              createdBy: String(userData.userId),
              UpdatedBy: null,
              ipAddress: ipAddress
            };
            delete createData.existingTransaction;
            await AxiosInstance().post("/loan-consent/transaction", createData);
          }
        } catch (error) {
          // Error handling
        }
      }
      return true;
    } catch (error) {
      message.error("Failed to submit consent data");
      return false;
    }
  };

  /* ------------------ STEP HANDLER ------------------ */
  const handleStepClick = async (step) => {
    const stepName = step.step?.name;

    /* ---------- KYC CONSENT VALIDATION ---------- */
    // Only validate consent for KYC step, not CKYC
    const subStepName = step.isMerged ? step.nextSubStep?.step?.name : stepName;
    if (subStepName === "KYC") {
      if (!areKycMandatoryConsentsAccepted()) {
        message.error("Please accept all mandatory consents");
        setShowKycConsentMsg(true);
        setTimeout(() => setShowKycConsentMsg(false), 3000);
        return;
      }

      // Submit consent data before proceeding
      const consentSubmitted = await submitKycConsentData();
      if (!consentSubmitted) {
        return;
      }
    }

    /* ---------- FRONTEND UI STEPS ---------- */
    if (step.isMerged && step.nextSubStep) {
      const subStepName = step.nextSubStep.step?.name;
      const pathMap = {
        "PAN VALIDATION": "pan-validation",
        "LOAN APPLICATION": "loan-form",
      };

      // Handle frontend UI steps with navigation
      if (pathMap[subStepName]) {
        navigate(`/phdashboard/loan/${pathMap[subStepName]}`, {
          state: {
            loanId: Number(id),
            stepId: step.nextSubStep.stepId,
            loanStepId: step.nextSubStep.loanStepId,
            userId: localStorage.getItem("userId"),
          },
        });
        return;
      }

      // Handle backend API steps
      setLoader(true);
      try {
        const payload = {
          loanId: Number(id),
          stepId: step.nextSubStep.stepId,
          loanStepId: step.nextSubStep.loanStepId,
          userId: Number(localStorage.getItem("userId")),
          data: {
            consentAt: new Date().toISOString(),
          },
        };

        const resp = await AxiosInstance().post("/policy/loan/step", payload);
        console.log("Step response:", resp);

        // Handle different response types
        if (resp.kycUrl || resp.data?.kycUrl || resp.data?.data) {
          const kycUrl = resp.kycUrl || resp.data?.kycUrl || resp.data?.data;
          if (kycUrl) {
            setLoader(false); // Reset loader before showing redirect UI
            setIsRedirecting(true);
            setTimeout(() => {
              window.location.href = kycUrl;
            }, 1500);
            return;
          }
        }

        if (resp.data?.url) {
          setLoader(false); // Reset loader before showing redirect UI
          setIsRedirecting(true);
          setTimeout(() => {
            window.location.href = resp.data.url;
          }, 1500);
          return;
        }

        // Show success message and refresh
        const successMsg = resp?.data?.msg || "Step completed successfully";
        message.success(successMsg);
        // Only fetch loan if the step was actually completed successfully
        if (successMsg?.toLowerCase().includes('success') || successMsg?.toLowerCase().includes('completed')) {
          fetchLoan();
        }
        setLoader(false); // Reset loader after processing
      } catch (error) {
        console.error("Step error:", error);
        message.error("Something went wrong. Please try again.");
        setLoader(false); // Reset loader on error
      }
      return;
    }
    
    if (["PAN VALIDATION", "LOAN APPLICATION"].includes(stepName)) {
      const pathMap = {
        "PAN VALIDATION": "pan-validation",
        "LOAN APPLICATION": "loan-form",
      };

      navigate(`/phdashboard/loan/${pathMap[stepName]}`, {
        state: {
          loanId: Number(id),
          stepId: step.stepId,
          loanStepId: step.loanStepId,
          userId: localStorage.getItem("userId"),
        },
      });
      return;
    }

    /* ---------- BACKEND / REDIRECT STEPS ---------- */
    setLoader(true);
    try {
      const payload = {
        loanId: Number(id),
        stepId: step.stepId,
        loanStepId: step.loanStepId,
        userId: Number(localStorage.getItem("userId")),
        data: {
          consentAt: new Date().toISOString(),
        },
      };

      const resp = await AxiosInstance().post("/policy/loan/step", payload);
      console.log("Step response:", resp);

      // Handle CKYC/KYC/VKYC responses (redirect to external URL)
      if (stepName === "CKYC" || stepName === "KYC" || stepName === "VKYC" || stepName === "V KYC") {
        // Try multiple ways to get the KYC URL
        let kycUrl = null;
        
        // Method 1: Direct kycUrl at top level
        if (resp.kycUrl) {
          kycUrl = resp.kycUrl;
        }
        // Method 2: kycUrl in data
        else if (resp.data?.kycUrl) {
          kycUrl = resp.data.kycUrl;
        }
        // Method 3: kycUrl in nested data
        else if (resp.data?.data?.kycUrl) {
          kycUrl = resp.data.data.kycUrl;
        }
        // Method 4: data object itself contains the URL
        else if (resp.data?.data && typeof resp.data.data === 'string') {
          kycUrl = resp.data.data;
        }
        // Method 5: url in data (for some API variations)
        else if (resp.data?.url) {
          kycUrl = resp.data.url;
        }

        if (kycUrl) {
          console.log("Redirecting to KYC URL:", kycUrl);
          setLoader(false); // Reset loader before showing redirect UI
          setIsRedirecting(true);
          // Small delay to show the loading state before redirect
          setTimeout(() => {
            window.location.href = kycUrl;
          }, 1500);
          return;
        } else {
          console.error("KYC URL not found in response:", resp);
          message.error("KYC URL not received. Please try again.");
        }
      }
      // Handle E Agreement/E Mandate responses
      else if (stepName === "E AGREEMENT" || stepName === "E MANDATE") {
        let redirectUrl = null;
        
        // Method 1: Direct url at top level
        if (resp.url) {
          redirectUrl = resp.url;
        }
        // Method 2: url in data
        else if (resp.data?.url) {
          redirectUrl = resp.data.url;
        }
        // Method 3: Check for success message with URL
        else if (resp.data?.msg === "Successfully Completed" && resp.data?.url) {
          redirectUrl = resp.data.url;
        }

        if (redirectUrl) {
          console.log("Redirecting to URL:", redirectUrl);
          setLoader(false); // Reset loader before showing redirect UI
          setIsRedirecting(true);
          // Small delay to show the loading state before redirect
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1500);
          return;
        } else {
          // If no URL but success message, just show success and refresh
          if (resp.data?.msg) {
            message.success(resp.data.msg);
            // Only fetch loan if the step was actually completed successfully
            if (resp.data?.msg?.toLowerCase().includes('success') || resp.data?.msg?.toLowerCase().includes('completed')) {
              fetchLoan();
            }
          } else {
            message.success("Step completed successfully");
            fetchLoan();
          }
          setLoader(false); // Reset loader after processing
        }
      }
      // Handle Application Submission
      else if (stepName === "APPLICATION SUBMISSION") {
        if (resp.data?.msg) {
          message.success(resp.data.msg);
          // Only fetch loan if the application was actually submitted successfully
          if (resp.data?.msg?.toLowerCase().includes('success') || resp.data?.msg?.toLowerCase().includes('submitted')) {
            fetchLoan();
          }
        } else {
          message.success("Application submitted successfully");
          fetchLoan();
        }
        fetchLoan();
        setLoader(false); // Reset loader after processing
      }
      // Handle any other step types
      else {
        // Check for redirect URL first
        let redirectUrl = resp.url || resp.data?.url;
        if (redirectUrl) {
          setLoader(false); // Reset loader before showing redirect UI
          setIsRedirecting(true);
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 1500);
          return;
        }
        
        // Otherwise show success message and refresh
        if (resp.data?.msg) {
          message.success(resp.data.msg);
          // Only fetch loan if the step was actually completed successfully
          if (resp.data?.msg?.toLowerCase().includes('success') || resp.data?.msg?.toLowerCase().includes('completed')) {
            fetchLoan();
          }
        } else {
          message.success("Step completed successfully");
          fetchLoan();
        }
        setLoader(false); // Reset loader after processing
      }
    } catch (error) {
      console.error("Step error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Something went wrong. Please try again.";
      message.error(errorMessage);
      setLoader(false); // Reset loader on error
    }
  };

  /* ------------------ LOADERS ------------------ */
  if (isReturning) {
    return (
      <div className="loader-screen redirect-loader">
        <div className="redirect-content">
          <div className="loader-animation">
            <svg className="loader-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#10b981" />
                  <stop offset="100%" stopColor="#059669" />
                </linearGradient>
              </defs>
              <circle className="loader-circle" cx="50" cy="50" r="45" />
              <circle className="loader-circle-progress" cx="50" cy="50" r="45" />
            </svg>
            <div className="company-logo">
              <div className="logo-icon">TPE</div>
            </div>
          </div>
          <div className="redirect-text">
            <h3>RETURNING TO TPE PLATFORM</h3>
            <p>Welcome back! Please wait while we load your loan application</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
            <small>Loading your application...</small>
          </div>
        </div>
      </div>
    );
  }

  if (!loanData) {
    return (
      <div className="loader-screen">
        <Spin size="large" />
        <p>Loading loan details…</p>
      </div>
    );
  }

  if (loader) {
    return (
      <div className="loader-screen">
        <Spin size="large" />
        <p>Processing your loan application</p>
        <small>Please do not refresh or close this tab.</small>
      </div>
    );
  }

  if (isRedirecting) {
    return (
      <div className="loader-screen redirect-loader">
        <div className="redirect-content">
          <div className="loader-animation">
            <svg className="loader-svg" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="100%" stopColor="#60a5fa" />
                </linearGradient>
              </defs>
              <circle className="loader-circle" cx="50" cy="50" r="45" />
              <circle className="loader-circle-progress" cx="50" cy="50" r="45" />
            </svg>
            <div className="company-logo">
              <div className="logo-icon">BFL</div>
            </div>
          </div>
          <div className="redirect-text">
            <h3>REDIRECTING TO BFL</h3>
            <p>Please wait while we connect you to the secure portal</p>
            <div className="loading-dots">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
            <small>Establishing secure connection...</small>
          </div>
        </div>
      </div>
    );
  }

  /* ------------------ TRANSFORM STEPS ------------------ */
  const transformSteps = (steps) => {
    const validationSteps = [];
    const kycSteps = [];
    const edataSteps = [];
    const otherSteps = [];
    
    steps.forEach(step => {
      const stepName = step.step?.name?.trim();
      const config = MERGED_STEPS_CONFIG[stepName];
      
      if (config) {
        if (config.group === "validation") {
          validationSteps.push({ ...step, ...config });
        } else if (config.group === "kyc") {
          kycSteps.push({ ...step, ...config });
        } else if (config.group === "edata") {
          edataSteps.push({ ...step, ...config });
        }
      } else {
        // Try case-insensitive matching as fallback
        const upperStepName = stepName?.toUpperCase();
        const fallbackConfigKey = Object.keys(MERGED_STEPS_CONFIG).find(key => 
          key.toUpperCase() === upperStepName
        );
        if (fallbackConfigKey) {
          const config = MERGED_STEPS_CONFIG[fallbackConfigKey];
          if (config.group === "validation") {
            validationSteps.push({ ...step, ...config });
          } else if (config.group === "kyc") {
            kycSteps.push({ ...step, ...config });
          } else if (config.group === "edata") {
            edataSteps.push({ ...step, ...config });
          }
        } else {
          otherSteps.push(step);
        }
      }
    });
    
    // Sort steps by order within each group
    validationSteps.sort((a, b) => a.order - b.order);
    kycSteps.sort((a, b) => a.order - b.order);
    edataSteps.sort((a, b) => a.order - b.order);
    
    const mergedSteps = [];
    
    // Create merged validation step
    if (validationSteps.length > 0) {
      const completedCount = validationSteps.filter(s => s.status === "COMPLETED").length;
      const progress = Math.round((completedCount / validationSteps.length) * 100);
      const isActive = validationSteps.some(s => s.isNext);
      const nextStep = validationSteps.find(s => s.isNext) || validationSteps.find(s => s.status !== "COMPLETED");
      
      const mergedValidationStep = {
        stepId: "merged-validation",
        step: { name: "Loan Application" },
        status: completedCount === validationSteps.length ? "COMPLETED" : isActive ? "IN PROGRESS" : "PENDING",
        isNext: isActive,
        isMerged: true,
        subSteps: validationSteps,
        progress,
        nextSubStep: nextStep
      };
      
      mergedSteps.push(mergedValidationStep);
    }
    
    // Create merged KYC step
    if (kycSteps.length > 0) {
      const completedCount = kycSteps.filter(s => s.status === "COMPLETED").length;
      const progress = Math.round((completedCount / kycSteps.length) * 100);
      const isActive = kycSteps.some(s => s.isNext);
      const nextStep = kycSteps.find(s => s.isNext) || kycSteps.find(s => s.status !== "COMPLETED");
      
      const mergedKycStep = {
        stepId: "merged-kyc",
        step: { name: "KYC" },
        status: completedCount === kycSteps.length ? "COMPLETED" : isActive ? "IN PROGRESS" : "PENDING",
        isNext: isActive,
        isMerged: true,
        subSteps: kycSteps,
        progress,
        nextSubStep: nextStep
      };
      
      mergedSteps.push(mergedKycStep);
    }
    
    // Create merged E-data step
    if (edataSteps.length > 0) {
      const completedCount = edataSteps.filter(s => s.status === "COMPLETED").length;
      const progress = Math.round((completedCount / edataSteps.length) * 100);
      const isActive = edataSteps.some(s => s.isNext);
      const nextStep = edataSteps.find(s => s.isNext) || edataSteps.find(s => s.status !== "COMPLETED");
      
      const mergedEdataStep = {
        stepId: "merged-edata",
        step: { name: "E-data & VKYC" },
        status: completedCount === edataSteps.length ? "COMPLETED" : isActive ? "IN PROGRESS" : "PENDING",
        isNext: isActive,
        isMerged: true,
        subSteps: edataSteps,
        progress,
        nextSubStep: nextStep
      };
      
      mergedSteps.push(mergedEdataStep);
    }
    
    // Add any remaining steps that weren't merged
    mergedSteps.push(...otherSteps);
    
    return mergedSteps;
  };

  const transformedSteps = loanData ? transformSteps(loanData.steps) : [];
  const activeStep = transformedSteps?.find((s) => s.isNext);

  /* ------------------ STEP HANDLER ------------------ */
  return (
    <section className="loan-track-page">
      <OtherPageHeader />

      <div className="container">
        <button className="back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>

        {/* Only show loan content if email is verified */}
        {emailVerified && loanData && (
          <>
            {/* ---------------- SUMMARY ---------------- */}
            <div className="loan-summary-card">
              <div><span>Policy No:</span> <b>{loanData.policy?.policyNumber}</b></div>
              <div><span>Loan Amount:</span> <b>₹ {loanData.requestedAmount || "-"}</b></div>
              <div><span>Loan ID:</span> <b>{loanData.loanId}</b></div>
              <div><span>Lending Partner:</span> <b>{loanData.bank?.name || "-"}</b></div>
              <div><span>Interest Rate Per Annum:</span> <b>{loanData.policy?.eligibleLoanInterestRate || "-"}%</b></div>
            </div>

            {/* ---------------- JOURNEY ---------------- */}
            <div className="loan-journey">

              {/* LEFT PANEL */}
              <div className="journey-left">
                <h4>Loan Journey</h4>

                <div className="stepper">
              {transformedSteps.map((step, index) => {
                const isCompleted = step.status === "COMPLETED";
                const isActive = step.isNext;

                return (
                  <div
                    key={step.stepId}
                    className={`stepper-item ${isCompleted ? "completed" : ""} ${isActive ? "active" : ""}`}
                  >
                    <div className="stepper-left">
                      <span className="step-dot" />
                      {index !== transformedSteps.length - 1 && (
                        <span className={`step-line ${isCompleted ? "line-completed" : ""}`} />
                      )}
                    </div>

                    <div className="stepper-content">
                      <div className="step-box">
                        <div className="step-header-content">
                          <div className="step-title-section">
                            <div className="step-title">
                              {step.step?.name}
                            </div>
                            {step.isMerged && (
                              <div className="progress-container">
                                <Progress 
                                  percent={step.progress} 
                                  size="small" 
                                  showInfo={false}
                                  strokeColor={{
                                    '0%': '#1677ff',
                                    '100%': '#0958d9',
                                  }}
                                />
                                <div className="sub-steps-info">
                                  {step.progress}% ({step.subSteps.filter(s => s.status === 'COMPLETED').length} of {step.subSteps.length} completed)
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="step-actions">
                            <span 
                              className={`status-pill status-pill-in-progress ${step.status.toLowerCase()}`}
                              style={{
                                ...(step.status === "IN PROGRESS" ? {
                                 
                                  cssText: "background: #f8fafc !important; color: #01955aff !important; border: 1px solid #01955aff !important; box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08) !important; font-size: 10px !important; padding: 4px 12px !important; border-radius: 999px !important; font-weight: 700 !important; letter-spacing: 0.3px !important; text-transform: uppercase !important; white-space: nowrap !important; min-width: 88px !important; text-align: center !important; line-height: 1 !important; flex-shrink: 0 !important; font-family: Lato, sans-serif !important; height: 19.47px !important;"
                                } : {})
                              }}
                            >
                              {step.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="journey-right">
            {/* <div className="step-header">
              <h3>{activeStep?.step?.name}</h3>
              <p>Complete the highlighted step to proceed</p>
            </div> */}

            <div className="step-workspace">
              {/* <div className="step-header text-center">
        <h2>{activeStep.step.name}</h2>
         <p>Complete the highlighted step to proceed</p>
      </div> */}

              {/* ---------- MERGED VALIDATION STEP UI COMPONENTS ---------- */}
              {activeStep?.isMerged && activeStep.nextSubStep?.step?.name === "LOAN APPLICATION" && (
                <LoanForm {...activeStep.nextSubStep} embedded />
              )}

              {activeStep?.isMerged && activeStep.nextSubStep?.step?.name === "PAN VALIDATION" && (
                <PanValidation {...activeStep.nextSubStep} embedded onSuccess={fetchLoan} />
              )}

              {/* ---------- MERGED KYC STEP UI COMPONENTS ---------- */}
              {activeStep?.isMerged && activeStep.step?.name === "KYC" && activeStep.nextSubStep && (
                <>
                  <div className="step-header text-center">
                    <h2>KYC Verification</h2>
                    <p>Complete your KYC verification to proceed with your loan application</p>
                  </div>
                  
                  {/* Show the appropriate UI component based on the current sub-step */}
                  {activeStep.nextSubStep.step?.name === "CKYC" && (
                    <div className="kyc-verification-container">
                      <Form layout="vertical">
                        <div className="form-section">
                          <h3 className="section-title">CKYC Verification</h3>
                          <p style={{ color: "#64748b", marginBottom: 24 }}>
                            Complete your CKYC verification to proceed with the loan application.
                          </p>
                          <div className="text-center">
                            <Button
                              type="primary"
                              size="large"
                              className="submit-button"
                              onClick={() => handleStepClick(activeStep)}
                            >
                              Start CKYC Verification
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}

                  {activeStep.nextSubStep.step?.name === "KYC" && (
                    <div className="kyc-verification-container">
                      <Form layout="vertical">
                        <div className="form-section">
                          <h3 className="section-title">KYC Verification</h3>
                          <p style={{ color: "#64748b", marginBottom: 24 }}>
                            Complete your KYC verification to proceed with the loan application.
                          </p>

                          {/* KYC Consents */}
                          <div className="mb-3">
                            {KYC_STATIC_CONSENTS.map((consent) => (
                              <div key={consent.key} style={{ marginBottom: "10px" }}>
                                <label style={{ display: "flex", alignItems: "flex-start", gap: "8px", cursor: "pointer", margin: 0 }}>
                                  <input
                                    type="checkbox"
                                    checked={kycConsentStates[consent.key] || false}
                                    onChange={(e) => handleKycConsentChange(consent.key, e.target.checked)}
                                    style={{ marginTop: "3px", flexShrink: 0, width: "14px", height: "14px", cursor: "pointer" }}
                                  />
                                  <span style={{ fontSize: "13px", lineHeight: "1.4", textAlign: "left" }}>
                                    {consent.message}
                                    {consent.isMandatory && (
                                      <span style={{ color: "red", marginLeft: "4px" }}>* (Required)</span>
                                    )}
                                  </span>
                                </label>
                              </div>
                            ))}
                          </div>

                          {showKycConsentMsg && (
                            <div style={{ marginTop: "10px", fontSize: "14px", color: "red", textAlign: "center" }}>
                              Please agree to the consent terms to proceed.
                            </div>
                          )}

                          <div className="text-center">
                            <Button
                              type="primary"
                              size="large"
                              className="submit-button"
                              onClick={() => handleStepClick(activeStep)}
                            >
                              Start KYC Verification
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}

                  {activeStep.nextSubStep.step?.name === "APPLICATION SUBMISSION" && (
                    <div className="application-submission-container">
                      <Form layout="vertical">
                        <div className="form-section">
                          <h3 className="section-title">Application Submission</h3>
                          <p style={{ color: "#64748b", marginBottom: 24 }}>
                            Submit your loan application to proceed with the final verification.
                          </p>
                          <div className="text-center">
                            <Button
                              type="primary"
                              size="large"
                              className="submit-button"
                              onClick={() => handleStepClick(activeStep)}
                            >
                              Submit Application
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}
                </>
              )}

              {/* ---------- MERGED E-DATA STEP UI COMPONENTS ---------- */}
              {activeStep?.isMerged && activeStep.step?.name === "E-data & VKYC" && activeStep.nextSubStep && (
                <>
                  <div className="step-header text-center">
                    <h2>E-data & VKYC</h2>
                    <p>Complete e-agreement, e-mandate, and VKYC verification to finalize your loan</p>
                  </div>
                  
                  {/* Show the appropriate UI component based on the current sub-step */}
                  {activeStep.nextSubStep.step?.name === "E AGREEMENT" && (
                    <div className="e-agreement-container">
                      <Form layout="vertical">
                        <div className="form-section">
                          <h3 className="section-title">E-Agreement</h3>
                          <p style={{ color: "#64748b", marginBottom: 24 }}>
                            Complete your e-agreement to proceed with the loan processing.
                          </p>
                          <div className="text-center">
                            <Button
                              type="primary"
                              size="large"
                              className="submit-button"
                              onClick={() => handleStepClick(activeStep)}
                            >
                              Complete E-Agreement
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}

                  {activeStep.nextSubStep.step?.name === "E MANDATE" && (
                    <div className="e-mandate-container">
                      <Form layout="vertical">
                        <div className="form-section">
                          <h3 className="section-title">E-Mandate</h3>
                          <p style={{ color: "#64748b", marginBottom: 24 }}>
                            Set up your e-mandate for automatic loan payments.
                          </p>
                          <div className="text-center">
                            <Button
                              type="primary"
                              size="large"
                              className="submit-button"
                              onClick={() => handleStepClick(activeStep)}
                            >
                              Set Up E-Mandate
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}

                  {(activeStep.nextSubStep.step?.name === "VKYC" || activeStep.nextSubStep.step?.name === "V KYC") && (
                    <div className="vkyc-verification-container">
                      <Form layout="vertical">
                        <div className="form-section">
                          <h3 className="section-title">VKYC Verification</h3>
                          <p style={{ color: "#64748b", marginBottom: 24 }}>
                            Complete your video KYC verification to finalize the loan process.
                          </p>
                          <div className="text-center">
                            <Button
                              type="primary"
                              size="large"
                              className="submit-button"
                              onClick={() => handleStepClick(activeStep)}
                            >
                              Start VKYC Verification
                            </Button>
                          </div>
                        </div>
                      </Form>
                    </div>
                  )}
                </>
              )}


              {/* ---------- PAN ---------- */}
              {activeStep?.step?.name === "PAN VALIDATION" && (
                <PanValidation {...activeStep} embedded onSuccess={fetchLoan} />
              )}

              {/* ---------- LOAN FORM ---------- */}
              {["LOAN APPLICATION"].includes(
                activeStep?.step?.name
              ) && (
                  <LoanForm {...activeStep} embedded />
                )}
            </div>
          </div>
        </div>
          </>
        )}

      {/* Email Verification Modal */}
      <Modal
        title="Email Verification Required"
        open={showEmailModal}
        closable={false}
        maskClosable={false}
        footer={null}
        width={600}
        centered
      >
        <EmailValidation
          loanId={id}
          onSuccess={handleEmailVerificationSuccess}
          embedded={true}
        />
      </Modal>
      </div>
    </section>
  );
}
